import type { RouteCondition } from "@/types/RouteConfig";
import { isSystemUser } from "@/utils/orgTypeUtils";
import { loginHasDirectBusiness, loginIsCommercialChannelPartner } from "@/utils/directBusiness";

/**
 * STIC-16004: route-level guards for the pages the sidebar hides by partner
 * variant, so a deep link cannot bypass the menu.
 *
 * They answer "may this login use these pages at all?", not "is this page
 * useful for the organisation currently browsed" - that second question is the
 * page's own, and the pages already answer it by rendering
 * `NotAvailableAtThisLevel` (see Invoicing.tsx). Keeping the two apart is what
 * lets a cross-level entry be greyed rather than bounced to /no_access.
 *
 * Deliberately synchronous, and deliberately blind to the browsed `?org=`.
 * Judging that organisation here would mean fetching it, and its answer belongs
 * to the page anyway: a partner browsing one of its customers gets the greyed
 * menu entry and the page's "not available" message, not a bounce to
 * /no_access.
 */

/**
 * Purchase orders, Support tickets, Billing and subscriptions, invoice and
 * quotation details: the organisation's own business with Sensolus. Sensolus
 * reads them for any organisation; everyone else only when its own organisation
 * deals with Sensolus directly (see directBusiness.ts).
 */
export const DIRECT_BUSINESS_ACCESS: RouteCondition = ({ loginInfo }) =>
  isSystemUser(loginInfo) || loginHasDirectBusiness(loginInfo);

/**
 * Demos and the Battery calculator: presales tools for Sensolus and for
 * commercial channel partners, at their own level. The partner Quotes add the
 * EU price list on top and keep their own condition in quoteAccess.ts.
 */
export const PRESALES_ACCESS: RouteCondition = ({ loginInfo }) =>
  isSystemUser(loginInfo) || loginIsCommercialChannelPartner(loginInfo);
